import { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Check, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const packages = [
  { id: '10', credits: 10, price: 15, perCredit: 1.50, popular: false },
  { id: '25', credits: 25, price: 30, perCredit: 1.20, popular: true },
  { id: '50', credits: 50, price: 50, perCredit: 1.00, popular: false },
  { id: '100', credits: 100, price: 85, perCredit: 0.85, popular: false },
];

export const PurchaseCreditsModal = ({ isOpen, onClose, onPurchaseSuccess }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [selectedPackage, setSelectedPackage] = useState('25');
  const [purchasing, setPurchasing] = useState(false);

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const response = await axios.post(
        `${API}/credits/purchase`,
        {
          package: selectedPackage,
          originUrl: window.location.origin
        },
        {
          headers: { 'X-Business-Id': user.id }
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkoutUrl;
    } catch (error) {
      console.error('Error purchasing credits:', error);
      toast.error('Error al komprar kréditos');
      setPurchasing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-manrope font-bold text-2xl flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-local-sage" />
            Komprar Kréditos
          </DialogTitle>
          <DialogDescription>
            Elige el paquete que mejor se adapte a tus necesidades. Publica kupones ilimitados y reduce tu komisión a 8%.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          {packages.map((pkg) => (
            <motion.button
              key={pkg.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedPackage(pkg.id)}
              className={`relative p-6 rounded-2xl border-2 transition-all text-left ${
                selectedPackage === pkg.id
                  ? 'border-local-sage bg-local-sage/5'
                  : 'border-border hover:border-local-sage/50'
              }`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-reward-gold text-white text-xs font-bold rounded-full">
                  Más Popular
                </div>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-3xl font-bold">{pkg.credits}</p>
                  <p className="text-sm text-muted-foreground">kréditos</p>
                </div>
                {selectedPackage === pkg.id && (
                  <div className="w-6 h-6 rounded-full bg-local-sage flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-2xl font-bold text-local-sage">{pkg.price}€</p>
                <p className="text-xs text-muted-foreground">
                  {pkg.perCredit}€ por krédito
                </p>
              </div>

              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-local-sage" />
                  <span>Komisión reducida al 8%</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-local-sage" />
                  <span>Kampañas IA básicas</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-local-sage" />
                  <span>Analytics básicos</span>
                </li>
              </ul>
            </motion.button>
          ))}
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-xl">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-local-sage flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold mb-1">Beneficios de los Kréditos:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Publica kupones sin límite mientras tengas kréditos</li>
                <li>• Komisión reducida del 12% al 8% en todas tus ventas</li>
                <li>• Kampañas de email automatizadas kon IA</li>
                <li>• Sin kompromiso mensual - paga solo lo que necesitas</li>
              </ul>
            </div>
          </div>
        </div>

        <Button
          onClick={handlePurchase}
          disabled={purchasing}
          className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-6 text-lg mt-4"
          data-testid="purchase-button"
        >
          {purchasing ? 'Procesando...' : `Komprar ${packages.find(p => p.id === selectedPackage)?.credits} Kréditos por ${packages.find(p => p.id === selectedPackage)?.price}€`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
